import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { BadgeCheck, type LucideIcon } from 'lucide-react';
import { initials } from '@/lib/format';

export const EASE = [0.22, 1, 0.36, 1] as const;

export const ROLE_LABEL: Record<string, string> = { SuperAdmin: 'Super Admin', Admin: 'IT Administrator', Auditor: 'Auditor', Employee: 'Employee' };

/** "Good morning" on the user's own clock. */
export function greeting(now = new Date()) {
  const h = now.getHours();
  return h < 5 ? 'Good evening' : h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/**
 * The personal welcome deck every role lands on: eyebrow, greeting with the first name, a line of context,
 * identity chips, and the avatar with its role badge. Entrance is one staged settle (eyebrow, greeting,
 * chips, side, then `children`), after which only the avatar ring keeps turning.
 */
export function WelcomeHero({
  icon: Icon,
  eyebrow,
  name,
  subtitle,
  chips,
  badge,
  actions,
  children,
}: {
  icon: LucideIcon;
  eyebrow: string;
  /** Full display name: the greeting uses the first word, the avatar the initials. */
  name: string;
  subtitle?: ReactNode;
  chips?: ReactNode[];
  /** Small pill under the avatar, e.g. the role. */
  badge?: ReactNode;
  actions?: ReactNode;
  /** Usually a row of stat tiles. */
  children?: ReactNode;
}) {
  const first = name.split(/\s+/)[0] ?? name;
  return (
    <section className="deck scope-dark my-hero mb-6 p-6 text-white sm:p-8" aria-labelledby="welcome-h">
      <div className="flex flex-col-reverse gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <motion.p
            className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] text-[#ff8a57] uppercase"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <Icon aria-hidden className="size-3.5" strokeWidth={2} /> {eyebrow}
          </motion.p>
          <motion.h1
            id="welcome-h"
            className="mt-3 text-[30px] leading-[1.1] font-semibold tracking-[-0.03em] text-white sm:text-[38px]"
            initial={{ opacity: 0, y: 14, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.06 }}
          >
            {greeting()}, <span className="my-hero-name">{first}</span>
          </motion.h1>
          {subtitle && (
            <motion.p className="mt-2 text-[14px] text-white/60" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.14 }}>
              {subtitle}
            </motion.p>
          )}
          {chips && chips.length > 0 && (
            <motion.div
              className="mt-4 flex flex-wrap items-center gap-2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE, delay: 0.18 }}
            >
              {chips.map((c, i) => (
                <span key={i} className="my-chip">
                  {c}
                </span>
              ))}
            </motion.div>
          )}
        </div>

        <motion.div
          className="flex shrink-0 items-center gap-4 sm:flex-col sm:items-center sm:gap-3"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.1 }}
        >
          <span className="my-avatar" aria-hidden>
            <span className="my-avatar-core">{initials(name)}</span>
          </span>
          {badge ?? (
            <span className="my-badge">
              <BadgeCheck aria-hidden className="size-3.5" /> Verified by SmartKargo IT
            </span>
          )}
        </motion.div>
      </div>

      {actions && (
        <motion.div className="mt-5 flex flex-wrap gap-2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE, delay: 0.22 }}>
          {actions}
        </motion.div>
      )}
      {children}
    </section>
  );
}
